import { useState, useEffect, useRef, useCallback, useMemo } from "react"
import { axios } from "@/contexts/auth"
import Modal from "@/components/modals/Modal"
import { ModalBody, ModalContent, ModalFooter } from "@nextui-org/modal"
import { toast } from "react-toastify"
import { ReactComponent as HeadphoneIcon } from "@/icons/headphone-icon.svg"
import { ReactComponent as ToolIcon } from "@/icons/tool-icon.svg"
import { ReactComponent as ShareIcon } from "@/icons/share-icon.svg"
import { ReactComponent as CloudIcon } from "@/icons/cloud-icon.svg"
import { ReactComponent as InfoIcon } from "@/icons/info-icon.svg"
import Button from "@/components/Button"
import { Preset } from "@/hooks/presets"
import LoadingSpinner from "@/components/LoadingSpinner"
import { Select, SelectItem } from "@nextui-org/select"
import { useFirmware, FIRMWARE_OUTDATED } from "@/hooks/firmware"
import { FIRMWARE_URL, DEVICE_CONNECTED } from "@/constants"
import { useLocation } from "react-router-dom"
import { NavLink } from "react-router-dom"
import { useAuth } from "@/contexts/auth"
import { useSerialDevice } from "@/contexts/serial-device"
import { useOPRA } from "@/hooks/opra"
import { createUnifiedPreset } from "@/utils/opra-converter"
import { BrandAutocomplete } from "@/components/forms/HeadphoneBrandAutocomplete"

type HeadphoneBrand = {
  id: number
  name: string
}

type Headphone = {
  id: number
  brand: HeadphoneBrand
  model: string
}

type Props = {
  isOpen: boolean
  onClose: () => void
  onPresetSelected: (preset: Preset) => void
  createPreset: (preset: {
    name: string
    mode: string
    config: object
    headphone?: {
      id: number
      brand: {
        id: number
        name: string
      }
      model: string
    } | null
    author?: string
    attribution_link?: string
    other_notes?: string
  }) => Promise<Preset | false>
}

type TabType = "community" | "opra"

const SharedPresetsModal = ({ isOpen, onClose, onPresetSelected, createPreset }: Props) => {
  const { isAuthenticated } = useAuth()
  const { status: deviceStatus } = useSerialDevice()

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>("community")

  // Community presets state (existing functionality)
  const [presets, setPresets] = useState([])
  const [brands, setBrands] = useState<HeadphoneBrand[]>([])
  const [headphones, setHeadphones] = useState<Headphone[]>([])
  const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null)
  const [selectedHeadphone, setSelectedHeadphone] = useState<Headphone | null>(null)
  const [selectedCreatorId, setSelectedCreatorId] = useState<number | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [nextPage, setNextPage] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // OPRA integration
  const {
    filteredPresets: opraPresets,
    brands: opraBrands,
    models: opraModels,
    authors: opraAuthors,
    isLoading: opraLoading,
    error: opraError,
    applyFilters: applyOPRAFilters,
  } = useOPRA()

  // OPRA filters
  const [opraBrandFilter, setOpraBrandFilter] = useState<string>("")
  const [opraModelFilter, setOpraModelFilter] = useState<string>("")
  const [opraAuthorFilter, setOpraAuthorFilter] = useState<string>("")

  // OPRA infinite scrolling
  const [opraDisplayedCount, setOpraDisplayedCount] = useState(50)
  const OPRA_ITEMS_PER_LOAD = 50

  // State for tracking expanded notes
  const [expandedNotes, setExpandedNotes] = useState<Set<string>>(new Set())

  // Function to toggle expanded notes
  const toggleExpandedNotes = (presetId: string) => {
    setExpandedNotes((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(presetId)) {
        newSet.delete(presetId)
      } else {
        newSet.add(presetId)
      }
      return newSet
    })
  }

  const { firmwareStatus, latestFirmware, deviceFirmwareVersion } = useFirmware()
  const location = useLocation()

  // Check if we're in the process of updating firmware
  const isFirmwareUpdating =
    location.pathname === FIRMWARE_URL &&
    document.querySelector('[class*="type-body-lg mt-5"]')?.textContent?.includes("firmware")

  // Fetch brands for the dropdown
  useEffect(() => {
    const fetchBrands = async () => {
      try {
        const response = await axios.get("/api/equalizer/headphone-brands/")
        setBrands(response.data)
      } catch (err) {
        console.error("Error fetching headphone brands:", err)
      }
    }
    fetchBrands()
  }, [])

  // Fetch headphones when brand changes
  useEffect(() => {
    const fetchHeadphones = async () => {
      // Only fetch if we have a brand selected
      if (!selectedBrandId) {
        setHeadphones([])
        return
      }

      try {
        const response = await axios.get(`/api/equalizer/headphones/`)
        // Filter headphones by selected brand and sort by model name
        const filteredHeadphones = response.data
          .filter((h) => h.brand.id === selectedBrandId)
          .sort((a, b) => a.model.localeCompare(b.model))
        setHeadphones(filteredHeadphones)
      } catch (err) {
        console.error("Error fetching headphones:", err)
      }
    }
    fetchHeadphones()
  }, [selectedBrandId])

  // Apply OPRA filters when they change
  useEffect(() => {
    applyOPRAFilters({
      brand: opraBrandFilter || undefined,
      model: opraModelFilter || undefined,
      author: opraAuthorFilter || undefined,
    })
    // Reset to first load when filters change
    setOpraDisplayedCount(OPRA_ITEMS_PER_LOAD)
  }, [opraBrandFilter, opraModelFilter, opraAuthorFilter, applyOPRAFilters])

  // Calculate displayed OPRA presets for infinite scrolling
  const displayedOpraPresets = useMemo(() => {
    return opraPresets.slice(0, opraDisplayedCount)
  }, [opraPresets, opraDisplayedCount])

  const opraHasMore = opraDisplayedCount < opraPresets.length

  // Create sorted arrays for dropdowns
  const sortedBrands = useMemo(
    () => brands.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [brands]
  )
  const sortedHeadphones = useMemo(
    () => headphones.slice().sort((a, b) => a.model.localeCompare(b.model)),
    [headphones]
  )
  const sortedOpraBrands = useMemo(
    () => opraBrands.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [opraBrands]
  )
  const sortedOpraModels = useMemo(
    () =>
      opraModels
        .filter(
          (model) =>
            !opraBrandFilter ||
            opraBrands.find((b) => b.name === opraBrandFilter)?.id === model.brandId
        )
        .sort((a, b) => a.name.localeCompare(b.name)),
    [opraModels, opraBrandFilter, opraBrands]
  )
  const sortedOpraAuthors = useMemo(
    () => opraAuthors.slice().sort((a, b) => a.name.localeCompare(b.name)),
    [opraAuthors]
  )

  // Load more OPRA presets for infinite scrolling
  const loadMoreOpraPresets = useCallback(() => {
    if (!opraHasMore || opraLoading) return

    setOpraDisplayedCount((prev) => prev + OPRA_ITEMS_PER_LOAD)
  }, [opraHasMore, opraLoading, OPRA_ITEMS_PER_LOAD])

  // Fetch community presets when modal opens or headphone selection changes
  useEffect(() => {
    const fetchPresets = async () => {
      if (activeTab !== "community") return

      setIsLoading(true)
      setHasMore(true)
      try {
        const params: { brand?: string; model?: string; user_id?: string } = {}
        if (selectedHeadphone) {
          params.model = selectedHeadphone.model
          params.brand = selectedHeadphone.brand.id.toString()
        } else if (selectedBrandId) {
          params.brand = selectedBrandId.toString()
        }
        if (selectedCreatorId) {
          params.user_id = selectedCreatorId.toString()
        }
        const response = await axios.get("/api/equalizer/public-presets/", { params })
        setPresets(response.data.results)
        setNextPage(response.data.next)
        setHasMore(!!response.data.next)
        console.log(`Initial load: ${response.data.results.length} presets loaded`)
      } catch (err) {
        console.error("Error fetching presets:", err)
      } finally {
        setIsLoading(false)
      }
    }

    if (isOpen) {
      fetchPresets()
    }
  }, [isOpen, selectedHeadphone, selectedBrandId, selectedCreatorId, activeTab])

  const loadMorePresets = useCallback(async () => {
    if (!nextPage || isLoading || !hasMore) return

    setIsLoading(true)
    try {
      const response = await axios.get(nextPage)
      setPresets((prev) => [...prev, ...response.data.results])
      setNextPage(response.data.next)
      setHasMore(!!response.data.next)
      console.log(
        `Loaded more: ${response.data.results.length} presets. Total: ${response.data.results.length + presets.length}`
      )
    } catch (err) {
      console.error("Error loading more presets:", err)
    } finally {
      setIsLoading(false)
    }
  }, [nextPage, isLoading, hasMore, presets.length])

  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current
    // Load more when user has scrolled to within 100px of the bottom
    if (scrollHeight - scrollTop - clientHeight < 100) {
      if (activeTab === "community") {
        loadMorePresets()
      } else if (activeTab === "opra") {
        loadMoreOpraPresets()
      }
    }
  }, [loadMorePresets, loadMoreOpraPresets, activeTab])

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll)
      return () => scrollContainer.removeEventListener("scroll", handleScroll)
    }
  }, [handleScroll])

  const handleListen = (preset: any) => {
    // Convert preset to unified format before passing to parent
    const unifiedPreset = createUnifiedPreset(preset)
    onPresetSelected(unifiedPreset)
    // Don't close the modal when listening
  }

  const handleSave = async (preset: any) => {
    setIsSaving(true)
    try {
      const unifiedPreset = createUnifiedPreset(preset)
      const isOPRAPreset = preset.source === "opra"

      console.log("Original preset headphone:", preset.headphone)

      // Create a new preset using the createPreset function from parent
      const newPreset = await createPreset({
        name: `${preset.name}${isOPRAPreset ? ` (${preset.author})` : ` (from ${preset.creator_name})`}`,
        mode: preset.mode || "headphone",
        config: unifiedPreset.config,
        headphone: preset.headphone || null,
        // For OPRA presets, set proper attribution
        author: isOPRAPreset ? preset.author : undefined,
        attribution_link: isOPRAPreset ? "https://github.com/opra-project/OPRA" : undefined,
        other_notes: isOPRAPreset
          ? `OPRA preset${preset.details ? ` - ${preset.details}` : ""}`
          : undefined,
      })

      console.log("Created new preset:", newPreset)

      if (newPreset) {
        toast.success("Preset saved successfully")
        // Load the saved preset to the device
        onPresetSelected(newPreset)
        onClose()
      } else {
        throw new Error("Failed to create preset")
      }
    } catch (err) {
      console.error("Error saving preset:", err)
      toast.error("Failed to save preset")
    } finally {
      setIsSaving(false)
    }
  }

  const handleShare = (preset: any, currentPage?: number) => {
    // Generate shareable URL with preset ID and source
    const shareUrl = `${window.location.origin}${window.location.pathname}?preset=${preset.id}&source=${preset.source || "community"}${currentPage ? `&page=${currentPage}` : ""}`
    navigator.clipboard.writeText(shareUrl)
    toast.success("Share link copied to clipboard")
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent className="h-[72vh] max-w-[72vw] bg-dark/40 backdrop-blur-sm">
        {(onClose) => (
          <>
            <ModalBody className="flex h-full flex-col overflow-hidden">
              <h2 className="type-body-xl font-bold text-white">Browse Presets</h2>

              {/* Tab buttons */}
              <div className="mb-4 flex border-b border-[#3D3D3D]">
                <button
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === "community"
                      ? "border-b-2 border-white text-white"
                      : "text-silver hover:text-white"
                  }`}
                  onClick={() => setActiveTab("community")}
                >
                  Core Community
                </button>
                <button
                  className={`px-4 py-2 font-medium transition-colors ${
                    activeTab === "opra"
                      ? "border-b-2 border-white text-white"
                      : "text-silver hover:text-white"
                  }`}
                  onClick={() => {
                    setActiveTab("opra")
                    setOpraDisplayedCount(OPRA_ITEMS_PER_LOAD) // Reset to first load when switching to OPRA tab
                  }}
                >
                  OPRA by Roon
                </button>
              </div>

              <div className="mt-3 flex flex-1 flex-col space-y-4 overflow-hidden">
                {/* Community Filters */}
                {activeTab === "community" && (
                  <div className="grid grid-cols-2 gap-x-4">
                    <div>
                      <div className="type-body-xs mb-2 text-silver">Filter by Brand</div>
                      <BrandAutocomplete
                        brands={sortedBrands}
                        value={selectedBrandId}
                        onChange={(brandId) => {
                          setSelectedBrandId(brandId)
                          // Clear headphone selection when brand changes
                          setSelectedHeadphone(null)
                        }}
                        placeholder="Any Brand"
                      />
                    </div>
                    <div>
                      <div className="type-body-xs mb-2 text-silver">Filter by Model</div>
                      <Select
                        selectedKeys={
                          selectedHeadphone ? new Set([selectedHeadphone.id.toString()]) : new Set()
                        }
                        onChange={(e) => {
                          const headphoneId = e.target.value ? parseInt(e.target.value) : null
                          const headphone = headphones.find((h) => h.id === headphoneId) || null
                          setSelectedHeadphone(headphone)
                        }}
                        isDisabled={!selectedBrandId}
                        aria-label="Filter by Model"
                        classNames={{
                          trigger:
                            "border border-[#383C4A] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F] transition-colors rounded-plus pl-4 bg-[#232528] min-h-7 h-7 shadow-auto-save-dropdown",
                          value:
                            "text-ghost group-data-[has-value=true]:text-white text-xs font-bulo font-bold",
                          popoverContent:
                            "bg-[#212121] rounded-plus border border-[#2E3037] p-0 shadow-outside text-ghost",
                          selectorIcon: "w-2.5 h-1.5",
                        }}
                        listboxProps={{
                          itemClasses: {
                            base: [
                              "!rounded-plus pl-3 py-1.5",
                              "data-[hover=true]:text-ghost",
                              "data-[hover=true]:bg-mine-shaft",
                              "data-[selectable=true]:focus:text-ghost",
                              "data-[selectable=true]:focus:bg-mine-shaft",
                            ],
                            title: "font-bulo text-xs font-bold",
                          },
                        }}
                        popoverProps={{
                          placement: "bottom-start",
                          motionProps: {
                            variants: {
                              initial: {
                                opacity: 0,
                                transform: "scaleY(0.9)",
                              },
                              enter: {
                                opacity: 1,
                                transform: "scaleY(1)",
                                transition: {
                                  type: "spring",
                                  bounce: 0,
                                  duration: 0.3,
                                },
                              },
                              exit: {
                                opacity: 0,
                                transform: "scaleY(0.96)",
                                transition: {
                                  type: "easeOut",
                                  bounce: 0,
                                  duration: 0.15,
                                },
                              },
                            },
                          },
                        }}
                        className="w-full"
                      >
                        <SelectItem key="none" value={null}>
                          Any Model
                        </SelectItem>
                        {
                          sortedHeadphones.map((headphone) => (
                            <SelectItem key={headphone.id} value={headphone.id}>
                              {headphone.model}
                            </SelectItem>
                          )) as any
                        }
                      </Select>
                    </div>
                  </div>
                )}

                {/* OPRA Filters */}
                {activeTab === "opra" && (
                  <div className="grid grid-cols-3 gap-x-4">
                    <div>
                      <div className="type-body-xs mb-2 text-silver">Filter by Brand</div>
                      <BrandAutocomplete
                        brands={sortedOpraBrands.map((brand) => ({
                          id: brand.name,
                          name: brand.name,
                        }))}
                        value={opraBrandFilter || null}
                        onChange={(brandName) => {
                          setOpraBrandFilter(brandName || "")
                          // Clear model filter when brand changes
                          setOpraModelFilter("")
                        }}
                        placeholder="Any Brand"
                      />
                    </div>
                    <div>
                      <div className="type-body-xs mb-2 text-silver">Filter by Model</div>
                      <Select
                        selectedKeys={
                          opraModelFilter ? new Set([opraModelFilter]) : new Set(["none"])
                        }
                        onSelectionChange={(keys) => {
                          const value = Array.from(keys)[0] as string
                          setOpraModelFilter(value === "none" ? "" : value)
                        }}
                        aria-label="Filter by Model"
                        classNames={{
                          trigger:
                            "border border-[#383C4A] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F] transition-colors rounded-plus pl-4 bg-[#232528] min-h-7 h-7 shadow-auto-save-dropdown",
                          value:
                            "text-ghost group-data-[has-value=true]:text-white text-xs font-bulo font-bold",
                          popoverContent:
                            "bg-[#212121] rounded-plus border border-[#2E3037] p-0 shadow-outside text-ghost",
                          selectorIcon: "w-2.5 h-1.5",
                        }}
                        className="w-full"
                      >
                        <SelectItem key="none">Any Model</SelectItem>
                        {
                          sortedOpraModels.map((model) => (
                            <SelectItem key={model.name}>{model.name}</SelectItem>
                          )) as any
                        }
                      </Select>
                    </div>
                    <div>
                      <div className="type-body-xs mb-2 text-silver">Filter by Author</div>
                      <Select
                        selectedKeys={
                          opraAuthorFilter ? new Set([opraAuthorFilter]) : new Set(["none"])
                        }
                        onSelectionChange={(keys) => {
                          const value = Array.from(keys)[0] as string
                          setOpraAuthorFilter(value === "none" ? "" : value)
                        }}
                        aria-label="Filter by Author"
                        classNames={{
                          trigger:
                            "border border-[#383C4A] data-[hover=true]:border-[#626675] data-[hover=true]:bg-[#272A2F] transition-colors rounded-plus pl-4 bg-[#232528] min-h-7 h-7 shadow-auto-save-dropdown",
                          value:
                            "text-ghost group-data-[has-value=true]:text-white text-xs font-bulo font-bold",
                          popoverContent:
                            "bg-[#212121] rounded-plus border border-[#2E3037] p-0 shadow-outside text-ghost",
                          selectorIcon: "w-2.5 h-1.5",
                        }}
                        className="w-full"
                        renderValue={(items) => {
                          // Custom render for selected value - show just author name
                          return items.map((item) => (
                            <span key={item.key}>
                              {String(item.key) === "none" ? "Any Author" : String(item.key)}
                            </span>
                          ))
                        }}
                      >
                        <SelectItem key="none">Any Author</SelectItem>
                        {
                          sortedOpraAuthors.map((author) => (
                            <SelectItem key={author.name} value={author.name}>
                              {author.name}
                              {!opraBrandFilter && !opraModelFilter ? ` (${author.count})` : ""}
                            </SelectItem>
                          )) as any
                        }
                      </Select>
                    </div>
                  </div>
                )}

                {!isAuthenticated() && (
                  <div className="flex items-center justify-between rounded-md border border-[#626675]/30 bg-[#272A2F]/40 px-4 py-2">
                    <div className="flex w-full items-center justify-center gap-2">
                      <span className="text-lg">⚠️</span>
                      <span className="type-body-base text-[#858484]">
                        <span className="font-bold">You are currently logged out.</span> Please log
                        in to Save Cloud Presets.
                      </span>
                    </div>
                  </div>
                )}

                {firmwareStatus === FIRMWARE_OUTDATED && !isFirmwareUpdating && (
                  <NavLink
                    to={FIRMWARE_URL}
                    className="type-body-base flex cursor-pointer items-center justify-center rounded-md border border-tundora py-2.5 pl-4 pr-6 font-bold transition-colors hover:border-silver"
                  >
                    <ToolIcon className="mr-4 w-5" />
                    FW
                    <span className="type-body-base ml-3 inline-block font-bold italic text-orange-400">
                      Please update to latest firmware to ensure compatibility with all presets! (
                      {deviceFirmwareVersion} &raquo; {latestFirmware.version})
                    </span>
                  </NavLink>
                )}

                {selectedCreatorId && (
                  <div className="flex items-center justify-between rounded-md border border-[#FFB800]/30 bg-[#FFB800]/5 px-4 py-2">
                    <span className="type-body-xs text-[#FFB800]">
                      Showing presets by {presets[0]?.creator_name}
                    </span>
                    <button
                      onClick={() => setSelectedCreatorId(null)}
                      className="type-body-xs flex items-center gap-x-1 text-[#FFB800] hover:text-white"
                    >
                      <span>×</span>
                      <span>Clear Filter</span>
                    </button>
                  </div>
                )}

                {/* OPRA Error Display */}
                {activeTab === "opra" && opraError && (
                  <div className="border-red-500/30 bg-red-500/5 flex items-center justify-between rounded-md border px-4 py-2">
                    <span className="type-body-xs text-red-400">
                      Error loading OPRA database: {opraError}
                    </span>
                  </div>
                )}

                <div
                  ref={scrollContainerRef}
                  className="flex-1 overflow-y-auto [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-jaguar hover:[&::-webkit-scrollbar-thumb]:bg-silver [&::-webkit-scrollbar-track]:bg-dark-jaguar/40 [&::-webkit-scrollbar]:w-2"
                >
                  {((activeTab === "community" && isLoading) ||
                    (activeTab === "opra" && opraLoading)) &&
                  (activeTab === "community" ? presets : opraPresets).length === 0 ? (
                    <div className="flex h-full items-center justify-center">
                      <span className="flex items-center gap-2">
                        <LoadingSpinner size="small" />
                        <span className="text-xs text-gray-500">
                          LOADING {activeTab === "community" ? "COMMUNITY" : "OPRA"} PRESETS
                        </span>
                      </span>
                    </div>
                  ) : (activeTab === "community" ? presets : opraPresets).length === 0 ? (
                    <p className="text-sm font-normal text-silver">
                      No presets found.{" "}
                      {activeTab === "community"
                        ? "Have one? Please share it!"
                        : "Try adjusting your filters."}
                    </p>
                  ) : (
                    <>
                      <div className="grid grid-cols-1 gap-4 p-1 sm:grid-cols-2 lg:grid-cols-3">
                        {(activeTab === "community" ? presets : displayedOpraPresets).map(
                          (preset) => (
                            <div
                              key={preset.id}
                              className="group flex flex-col rounded-plus border border-[#3D3D3D] bg-[#2B2B2B]/40 p-4 transition-colors duration-200 hover:border-[#4A4A4A] hover:bg-mine-shaft/40"
                            >
                              {activeTab === "opra" ? (
                                // OPRA preset layout: <brand> <name>, <type>, by: <author> - <details>
                                <>
                                  <div className="flex items-center justify-between">
                                    <h2 className="type-body-sm font-medium text-white">
                                      {preset.headphone ? (
                                        <>
                                          {preset.headphone.brand.name} {preset.headphone.model}
                                        </>
                                      ) : (
                                        <>
                                          {preset.productType && preset.productSubtype
                                            ? `${preset.productType.charAt(0).toUpperCase() + preset.productType.slice(1)} - ${preset.productSubtype.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())}`
                                            : preset.name}
                                        </>
                                      )}
                                    </h2>
                                  </div>
                                  <h3 className="type-body-xs mt-1 font-medium capitalize text-silver">
                                    Type:{" "}
                                    {preset.productSubtype
                                      ? preset.productSubtype
                                          .replace(/_/g, " ")
                                          .replace(/\b\w/g, (l) => l.toUpperCase())
                                      : "In ear"}
                                  </h3>
                                  <div className="type-body-xs mt-1 text-[#858484]">
                                    Source:{" "}
                                    <span className="font-bold text-[#858484]">
                                      {preset.author || "Unknown"}
                                    </span>
                                    {preset.author === "oratory1990" && (
                                      <span className="ml-2">
                                        <a
                                          href="http://www.buymeacoffee.com/oratory1990"
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center text-xs text-[#FFDD00] transition-colors hover:text-[#FFDD00]/80"
                                          title="Support oratory1990's work"
                                        >
                                          ☕
                                        </a>
                                      </span>
                                    )}
                                    {preset.details ? ` - ${preset.details}` : ""}
                                  </div>
                                </>
                              ) : (
                                // Community preset layout
                                <>
                                  <div className="flex items-center justify-between">
                                    <h2 className="type-body-sm font-medium text-white">
                                      {preset.name}
                                    </h2>
                                  </div>
                                  <h3 className="type-body-xs mt-1 font-medium text-silver">
                                    {preset.headphone ? (
                                      <>
                                        {preset.headphone.brand.name} {preset.headphone.model}
                                      </>
                                    ) : (
                                      "No headphone specified"
                                    )}
                                  </h3>
                                  {/* Source info with creator */}
                                  <div className="type-body-xs mt-1 text-[#858484]">
                                    by:{" "}
                                    <button
                                      onClick={() => {
                                        setSelectedCreatorId(preset.creator_id)
                                      }}
                                      className={`type-body-xs ${
                                        preset.is_official
                                          ? "font-bold text-[#FFB800] hover:text-[#FFB800]/80"
                                          : "font-bold text-[#858484] hover:text-white"
                                      } transition-colors`}
                                    >
                                      {preset.creator_name}
                                    </button>
                                    {preset.author && (
                                      <>
                                        <span> - Source </span>
                                        {preset.attribution_link ? (
                                          <>
                                            <a
                                              href={preset.attribution_link}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="font-bold text-blue underline transition-colors hover:text-brightest-blue"
                                            >
                                              {preset.author}
                                            </a>
                                            {preset.author === "oratory1990" && (
                                              <span className="ml-2">
                                                <a
                                                  href="http://www.buymeacoffee.com/oratory1990"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center text-xs text-[#FFDD00] transition-colors hover:text-[#FFDD00]/80"
                                                  title="Support oratory1990's work"
                                                >
                                                  ☕
                                                </a>
                                              </span>
                                            )}
                                          </>
                                        ) : (
                                          <>
                                            <span className="font-bold text-[#858484]">
                                              {preset.author}
                                            </span>
                                            {preset.author === "oratory1990" && (
                                              <span className="ml-2">
                                                <a
                                                  href="http://www.buymeacoffee.com/oratory1990"
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  className="inline-flex items-center text-xs text-[#FFDD00] transition-colors hover:text-[#FFDD00]/80"
                                                  title="Support oratory1990's work"
                                                >
                                                  ☕
                                                </a>
                                              </span>
                                            )}
                                          </>
                                        )}
                                      </>
                                    )}
                                    {(preset.attribution_link || preset.other_notes) && (
                                      <button
                                        onClick={() => toggleExpandedNotes(preset.id.toString())}
                                        className="ml-1 cursor-pointer text-[#858484] transition-colors hover:text-silver"
                                        title="Show additional information"
                                      >
                                        <InfoIcon className="inline h-3 w-3" />
                                      </button>
                                    )}
                                  </div>
                                  {/* Expandable details */}
                                  {expandedNotes.has(preset.id.toString()) &&
                                    preset.other_notes && (
                                      <div className="mt-2">
                                        <div className="type-body-xs text-silver">
                                          <span className="font-medium">Notes:</span>{" "}
                                          {preset.other_notes}
                                        </div>
                                      </div>
                                    )}
                                </>
                              )}
                              <div className="mt-4 flex items-center gap-x-2">
                                <button
                                  className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white disabled:opacity-50"
                                  onClick={() => handleListen(preset)}
                                >
                                  <HeadphoneIcon className="h-3 w-3" />
                                  Listen
                                </button>
                                <button
                                  className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white disabled:opacity-50"
                                  onClick={() => handleSave(preset)}
                                  disabled={
                                    isSaving ||
                                    !isAuthenticated() ||
                                    deviceStatus !== DEVICE_CONNECTED
                                  }
                                >
                                  <CloudIcon className="h-3 w-3" />
                                  {isSaving ? "Saving..." : "Save to Presets"}
                                </button>
                                {/* Only show Share button for Community presets */}
                                {activeTab === "community" && (
                                  <button
                                    className="type-body-xs flex items-center gap-x-1.5 rounded-plus border border-jaguar bg-dark-jaguar/40 px-3 py-1.5 font-medium text-silver transition-colors hover:border-silver hover:text-white"
                                    onClick={() =>
                                      handleShare(
                                        preset,
                                        Math.floor(presets.indexOf(preset) / 50) + 1
                                      )
                                    }
                                  >
                                    <ShareIcon className="h-3 w-3" />
                                    Share
                                  </button>
                                )}
                              </div>
                            </div>
                          )
                        )}
                      </div>
                      {((activeTab === "community" && isLoading) ||
                        (activeTab === "opra" &&
                          opraLoading &&
                          opraDisplayedCount > OPRA_ITEMS_PER_LOAD)) && (
                        <div className="flex justify-center py-4">
                          <span className="flex items-center gap-2">
                            <LoadingSpinner size="small" />
                            <span className="text-xs text-gray-500">LOADING MORE...</span>
                          </span>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </ModalBody>
            <ModalFooter className="flex items-center justify-between gap-x-4">
              {/* Left side - OPRA Attribution when active, empty div when Community tab */}
              <div className="flex items-center gap-x-3">
                {activeTab === "opra" && (
                  <>
                    <img src="/opra_logo.png" alt="OPRA Logo" className="h-6 w-auto invert" />
                    <div className="text-xs text-silver">
                      <span>
                        OPRA is an open, community-maintained directory of product information and
                        EQ compensation curves that optimize a wide range of headphone models.
                        <br />
                        For more information or to contribute{" "}
                        <a
                          href="https://github.com/opra-project/OPRA"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline transition-colors hover:text-white"
                        >
                          see here
                        </a>
                        .
                      </span>
                    </div>
                  </>
                )}
              </div>

              <Button
                className="-mr-5"
                theme="none"
                size="sm"
                outline={false}
                solid={true}
                full={false}
                onClick={onClose}
              >
                Close
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  )
}

export default SharedPresetsModal
